import React from 'react'
import {
  Modal,
  ModalContent,
  ModalClose,
  Button,
  IconLogoCombined,
  Loader,
} from '@mc/design-system'
import { useTranslation, Trans } from '@mc/i18n'
import { paths, pathsWithQueryParams } from '@mc/paths'
import { getAccountId } from '@mc/user-info'
import { PROMO_I18N_NAMESPACE } from '../../lib/constants'
import { getLocalizedText } from '../../lib/helpers'
import {
  DiscountModalContextProvider,
  useDiscountModal,
} from './components/DiscountModalContext'
import { PromotionAppliedOnErrorCta } from './components/PromotionAppliedOnErrorCta'
import { PromotionAppliedOnErrorText } from './components/PromotionAppliedOnErrorText'
import { CTA_ACTIONS, SIRIUS_PROMO_CODE } from './constants'
import { trackOfferTermsClick } from './events'
import { getIsGift } from './utils'

export const DiscountModal = () => (
  <DiscountModalContextProvider>
    <DiscountModalContainer />
  </DiscountModalContextProvider>
)

const DiscountModalContainer = () => {
  const { showModal, onClose } = useDiscountModal()
  const { t } = useTranslation(PROMO_I18N_NAMESPACE)

  return (
    <Modal
      size='small'
      show={showModal}
      onClose={() => onClose(CTA_ACTIONS.dismiss)}
    >
      <ModalContent
        aria-label={t('common.home.modals.discount.a11y.title')}
        className='mc-bg-light'
      >
        <div className='mc-theme-light mc-px-5 mc-py-9 mc-text--center'>
          <ModalClose />
          <IconLogoCombined
            className='mc-mb-4 mc-text-color--dark mc-icon--scale-11'
            viewBox='0 0 66 50'
          />
          <DiscountModalContent />
        </div>
      </ModalContent>
    </Modal>
  )
}

const DiscountModalContent = () => {
  const { discount, isLoading } = useDiscountModal()
  const { t } = useTranslation(PROMO_I18N_NAMESPACE)

  const termsLink =
    discount?.slug === SIRIUS_PROMO_CODE
      ? 'https://learn.masterclass.com/sirius-offer-terms'
      : discount?.termsLink

  if (isLoading) return <Loader color={'#E32652'} />

  if (!discount) return null

  if (discount.error) {
    return (
      <>
        {discount?.showPromotionAppliedOnError ? (
          <>
            <h1 className='mc-mb-3 mc-text-h4'>
              {t('common.home.modals.discount.promotionAppliedOnError.title')}
            </h1>
            <div className='mc-mb-8 mc-px-6'>
              <PromotionAppliedOnErrorText error={discount.error} />
            </div>
            <PromotionAppliedOnErrorCta />
          </>
        ) : (
          <>
            <h1 className='mc-mb-5 mc-text-h6'>{discount.error}</h1>
            <DiscountModalCTAs />
          </>
        )}
      </>
    )
  }

  return (
    <>
      {!!discount.intro && (
        <p className='mc-mb-3 mc-text-large'>{discount.intro}</p>
      )}
      <h1 className='mc-mb-3 mc-text-h4'>
        {discount.modalTitle
          ? getLocalizedText(discount.modalTitle)
          : t('common.home.modals.discount.title')}
      </h1>
      <p className='mc-mb-8 mc-px-6'>
        {discount.modalText ? (
          getLocalizedText(discount.modalText)
        ) : (
          <Trans
            i18nKey='common.home.modals.discount.description'
            values={{ discountValue: discount.discountValue }}
            ns={PROMO_I18N_NAMESPACE}
          >
            <b />
          </Trans>
        )}
      </p>
      {!!termsLink && (
        <p className='mc-mb-5 mc-font-caption mc-opacity--muted'>
          <Trans
            i18nKey='common.home.modals.discount.offerTerms'
            ns={PROMO_I18N_NAMESPACE}
          >
            <a
              className='mc-text--link'
              onClick={() => trackOfferTermsClick(discount)}
              href={termsLink}
              rel='noopener noreferrer'
              target='_blank'
            />
          </Trans>
        </p>
      )}
      <DiscountModalCTAs />
    </>
  )
}

const DiscountModalCTAs = () => {
  const { discount, onClose, query, pathname } = useDiscountModal()
  const { t } = useTranslation(PROMO_I18N_NAMESPACE)

  const isGift = getIsGift({ discount, pathname })

  if (!discount) return null

  if (discount.error) {
    return (
      <Button
        accessibleWhenDisabled
        fullWidth
        className='mc-mb-3'
        onClick={() => onClose(CTA_ACTIONS.primary)}
      >
        {t('common.home.modals.discount.cta')}
      </Button>
    )
  }

  if (discount?.requireLogin && !getAccountId()) {
    return (
      <Button
        render={
          <a
            href={paths.logIn(
              pathsWithQueryParams.checkout({
                isGift,
                query,
              }),
            )}
          />
        }
        accessibleWhenDisabled
        fullWidth
        className='mc-mb-3'
        onClick={() => onClose(CTA_ACTIONS.primary)}
      >
        {t('common.home.modals.discount.loginCta')}
      </Button>
    )
  }

  const ctaText = discount.modalCtaText
    ? getLocalizedText(discount.modalCtaText)
    : t('common.home.modals.discount.plansCta', {
        discountValue: discount.discountValue,
      })

  return (
    <Button
      render={
        <a
          href={pathsWithQueryParams.checkout({
            isGift,
            query,
          })}
        />
      }
      accessibleWhenDisabled
      fullWidth
      className='mc-mb-3'
      onClick={() => onClose(ctaText)}
    >
      {ctaText}
    </Button>
  )
}

export default DiscountModal
